/**
 * Who has been reading your site.
 *
 * The biggest uncovered area in the app: thirty-six components, nine thousand
 * lines, and the thing customers name when they say what they pay for. Most
 * of it needs no new parts. Activity over time is a Trend, which bots came is
 * a Share, the visit log is a Table, an observation is an Insight, and the
 * install snippet is a Paste. Five things here are genuinely about bots.
 *
 * The area answers one question in four parts, and the order matters: is it
 * working, has anything come, who were they, and what did they read. The app
 * opens with four animated stat cards, which answers the third part first and
 * the first part not at all — so somebody who has just installed the pixel and
 * seen nothing has no way to tell a working setup from a broken one.
 */
import { type ComponentType, type CSSProperties, type ReactNode } from 'react'
import { ChatGPTLogo, ClaudeLogo, GeminiLogo, PerplexityLogo, type AILogoProps } from './botmarks'
import { Check, Circle, Dot, Eye, RadioTower, TriangleAlert } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Fig } from './primitives'
import { useArrived } from './media'
import './bots.css'

/* ---------------- Is it working ---------------- */

/**
 * The trust anchor for the whole feature.
 *
 * Until a bot turns up there is nothing else on the page that can tell
 * somebody their pixel is installed correctly, and the wait is measured in
 * days. This is the component that makes the empty weeks survivable.
 */
export function Heartbeat({
  state,
  said,
  when,
  children,
}: {
  /** live: we are receiving. quiet: installed, nothing yet. off: we hear nothing. */
  state: 'live' | 'quiet' | 'off'
  said: string
  when?: ReactNode
  children?: ReactNode
}) {
  const Icon = state === 'off' ? TriangleAlert : state === 'quiet' ? RadioTower : Check
  return (
    <div className={cn('ds-heartbeat', state === 'quiet' && 'ds-heartbeat--quiet', state === 'off' && 'ds-heartbeat--off')}>
      <span className="ds-heartbeat__mark" aria-hidden="true">
        <Icon />
      </span>
      <div className="ds-heartbeat__body">
        <div className="ds-heartbeat__said">{said}</div>
        {when && <div className="ds-heartbeat__when">{when}</div>}
        {children && <div className="ds-heartbeat__foot">{children}</div>}
      </div>
    </div>
  )
}

/* ---------------- Who a bot is ---------------- */

/**
 * "GPTBot" is a string nobody recognises. What a reader needs is whose it is
 * and what it does with a page, which is the same three lines every time.
 *
 * Replaces BotProfileCard and BotBadge, the latter of which drew six
 * categories in six hues — blue for training, green for search, purple for
 * research, orange for shopping, pink for social. A category is not a
 * severity, so none of that colour could be read as meaning anything.
 */
/*
  The marks of the agents that read a customer's site.

  One registry, so a mark is defined once and every surface that names an
  agent draws the same one: the bot list on the home page, a bot card, the
  onboarding shell, the score visuals, the domain page.

  These are other companies' trademarks and they are the real ones, drawn from
  the SVGs the in-app bot tracking library already used. They are never
  approximated: a mark redrawn from memory misrepresents somebody else's
  brand, which is the same rule that stops this product inventing a fact about
  a customer. An agent with no mark registered falls back to its initial.

  They keep their own colour, like our own mark does. A supplied brand object
  is not something the interface draws, so the rule that colour only ever
  means status does not reach it.
*/
export const BOT_MARKS: Record<string, ComponentType<AILogoProps>> = {
  ChatGPT: ChatGPTLogo,
  GPTBot: ChatGPTLogo,
  'OAI-SearchBot': ChatGPTLogo,
  Claude: ClaudeLogo,
  ClaudeBot: ClaudeLogo,
  Gemini: GeminiLogo,
  'Google-Extended': GeminiLogo,
  Perplexity: PerplexityLogo,
  PerplexityBot: PerplexityLogo,
}

/** The tile beside an agent's name: its mark, or its initial if we have none. */
export function BotMark({ name }: { name: string }) {
  const Mark = BOT_MARKS[name]
  return (
    <span className="ds-botmark" aria-hidden="true">
      {Mark ? <Mark brand ariaLabel="" /> : name.slice(0, 1).toUpperCase()}
    </span>
  )
}

export function BotFace({
  name,
  whose,
  does,
  visits,
}: {
  name: string
  /** The company behind it, in their own name. */
  whose: string
  /** What it does with a page, in a sentence. */
  does?: string
  visits?: number
}) {
  return (
    <div className="ds-bot">
      <BotMark name={name} />
      <div className="ds-bot__body">
        <div className="ds-bot__name">{name}</div>
        <div className="ds-bot__whose">{whose}</div>
        {does && <p className="ds-bot__does">{does}</p>}
      </div>
      {visits !== undefined && (
        <div className="ds-bot__count">
          <div className="ds-bot__visits">
            <Fig>{visits}</Fig>
          </div>
          <div className="ds-bot__visits-said">{visits === 1 ? 'visit' : 'visits'}</div>
        </div>
      )}
    </div>
  )
}

/* ---------------- The first one ---------------- */

/**
 * The moment the feature proves itself.
 *
 * The app fires confetti here. The customer is not celebrating our product;
 * they are finding out that AI has started reading their business, which is a
 * bigger thing than a party and is better served by saying it plainly and
 * showing them exactly what happened.
 */
export function Seen({
  said,
  what,
  facts,
  children,
}: {
  said: string
  what?: string
  /** What actually happened, so the claim can be checked rather than believed. */
  facts: { label: string; value: ReactNode }[]
  children?: ReactNode
}) {
  return (
    <div className="ds-seen">
      <span className="ds-seen__mark" aria-hidden="true">
        <Eye />
      </span>
      <div className="ds-seen__said">{said}</div>
      {what && <p className="ds-seen__what">{what}</p>}
      <dl className="ds-seen__facts">
        {facts.map((f) => (
          <div key={f.label} className="ds-seen__fact">
            <dt>{f.label}</dt>
            <dd>{f.value}</dd>
          </div>
        ))}
      </dl>
      {children && <div className="ds-seen__foot">{children}</div>}
    </div>
  )
}

/* ---------------- Nothing yet ---------------- */

export interface WaitStep {
  said: string
  note?: string
  state: 'done' | 'now' | 'next'
}

/**
 * The longest state anybody sits in, and the one the app handles worst: an
 * empty dashboard of zeroes.
 *
 * A zero drawn as a statistic reads as failure. This says what is true, where
 * they are in a process that takes days, and what is happening on other
 * people's sites meanwhile — which is the only honest reassurance available
 * while their own site has nothing to show.
 */
export function Waiting({
  said,
  what,
  steps,
  elsewhere,
}: {
  said: string
  what?: string
  steps: WaitStep[]
  /**
   * Our own platform numbers. The source is required for the same reason it
   * is required on a marketing page: a number nobody can trace is a claim.
   */
  elsewhere?: { said: ReactNode; from: string }
}) {
  return (
    <div className="ds-waiting">
      <div className="ds-waiting__said">{said}</div>
      {what && <p className="ds-waiting__what">{what}</p>}
      <ul className="ds-waiting__steps">
        {steps.map((s) => (
          <li key={s.said} className={cn('ds-waiting__step', `ds-waiting__step--${s.state}`)}>
            {s.state === 'done' ? <Check aria-hidden="true" /> : s.state === 'now' ? <Dot aria-hidden="true" /> : <Circle aria-hidden="true" />}
            <span>
              <b>{s.said}</b>
              {s.note && <span>{s.note}</span>}
            </span>
          </li>
        ))}
      </ul>
      {elsewhere && (
        <div className="ds-waiting__else">
          {elsewhere.said}
          <div className="ds-waiting__from">{elsewhere.from}</div>
        </div>
      )}
    </div>
  )
}

/* ---------------- What they read most ---------------- */

/**
 * A page and a bar. Replaces TopPagesTable, whose column headings were set in
 * uppercase monospace with letter-spacing, which the type rules forbid
 * outright: monospace belongs to timestamps and to markup, and nowhere else.
 */
export function Ranked({ rows }: { rows: { what: string; n: number; mark?: ReactNode }[] }) {
  const top = Math.max(...rows.map((r) => r.n), 1)
  // Each bar grows to its share once, the first time the list is on screen:
  // the same arrival the score bars and the dial use, by the same hook.
  const [ref, arrived] = useArrived<HTMLUListElement>()
  return (
    <ul className="ds-ranked" ref={ref} data-arrived={arrived || undefined}>
      {rows.map((r) => (
        <li key={r.what} className="ds-ranked__row" data-marked={r.mark ? 'true' : undefined}>
          <span className="ds-ranked__what">
            {r.mark}
            <span>{r.what}</span>
          </span>
          <span className="ds-ranked__n">
            <Fig>{r.n}</Fig>
          </span>
          <span className="ds-ranked__bar" aria-hidden="true">
            <span style={{ '--ds-ranked-v': Math.max(0.02, r.n / top) } as CSSProperties} />
          </span>
        </li>
      ))}
    </ul>
  )
}
